import {useEffect, useRef} from 'react'

import {useCopilotUiReadiness} from './copilot-ui-readiness-context'
import type {UiReadinessSurface} from './ui-readiness-contract'
import {userErrorMetrics, type UserErrorCode} from './user-visible-errors'

/**
 * Counts an error shown to the user once per distinct error. Pass `undefined` while no error is
 * shown; a new `identity` (such as a failure generation) counts the same code again.
 */
export function useReportUserVisibleError(
  code: UserErrorCode | undefined,
  {surface, taskId, identity}: {surface?: UiReadinessSurface; taskId?: string; identity?: string | number} = {},
) {
  const readinessSurface = useCopilotUiReadiness()?.open.surface
  const resolvedSurface = surface && surface !== 'unknown' ? surface : (readinessSurface ?? surface)
  const reportedRef = useRef<string | undefined>(undefined)
  const key = code ? `${code}:${identity ?? ''}` : undefined
  useEffect(() => {
    if (!key || !code) {
      reportedRef.current = undefined
      return
    }
    if (reportedRef.current === key) return
    reportedRef.current = key
    try {
      userErrorMetrics.shown({code, surface: resolvedSurface, taskId})
    } catch {
      // Telemetry must not affect error rendering.
    }
  }, [key, code, resolvedSurface, taskId])
}
